import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import EntityStats from "../../model/EntityStats";
import ChemiNovaEntityType from "../../model/enums/ChemiNovaEntityType";
import PagedData from "../../model/PagedData";
import { User } from "../../model/User";
import { IApiClientError } from "../../shared/ApiClient";
import APIEndPoints from "../../utils/APIEndPoints";
import AppIcons from "../../utils/AppIcons";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ImgUtil from "../../utils/ImgUtil";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import ButtonInfo from "../common/button/ButtonInfo";
import ButtonType from "../common/button/ButtonType";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import SelectSearchOption from "../common/select_search/SelectSearchOption";
import SelectWithSearch from "../common/select_search/SelectSearchSide";
import MyList from "../../model/MyList";
import GroupingType from "../../model/enums/GroupingType";

function MyListDetails() {
  // read the ID from the url
  const { id } = useParams();

  // reference to the AuthManager
  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<MyList>(MyList.EmptyInstance());
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // indicate if a refresh is necessary
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  // add users to share
  const [addUsersToShare, setAddUsersToShare] = useState(false);
  const [candidateUserID, setCandidateUserID] = useState(-1);
  // list of candidate users
  const [candidateUsersList, setCandidateUsersList] = useState<User[]>([]);
  // list of users already in the shared list
  const [usersAlreadySharing, setUsersAlreadySharing] = useState<Set<number>>();
  // keyword to search for users in DB
  const [keywordUsers, setKeywordUsers] = useState("");

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let myList: MyList | undefined = undefined;
        if (id) {
          myList = await apiClient?.get<MyList>({
            url: APIEndPoints.MY_LISTS.ONE(parseInt(id)),
          });
          setData(myList);
          setUsersAlreadySharing(new Set(myList.users?.map((u) => u.id)));
        }
      } catch (error: any) {}
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id, refreshNeeded]);

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.MY_LISTS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.MY_LISTS }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete this record",
          });
        }
      });
  };

  const removeAssetFromCollection = async (assetId: number) => {
    if (!id) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "Cannot access database records",
      });
    } else {
      // initialize the list of the items that will remain in this group
      data.id_chelements = [];
      if (data.chelements) {
        // filter out the element to be removed
        data.id_chelements = data?.chelements
          .filter((e) => e.id !== assetId)
          .map((e) => e.id!);
      }

      apiClient
        ?.put({
          url: APIEndPoints.MY_LISTS.ONE(parseInt(id)),
          body: data,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Asset successfully removed from this list.",
          });
          setRefreshNeeded(!refreshNeeded);
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            alertsManager.showAlertMessage({
              type: AlertMessageType.ERROR,
              message: "Unable to implement the remove action.",
            });
          }
        });
    }
  };

  const removeUserFromList = (userId: number) => {
    if (!id) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "Cannot access database records",
      });
    } else {
      // initialize the list of users that will remain in this list
      data.id_users = [];
      if (data.users) {
        // filter out the user to be removed
        data.id_users = data?.users
          .filter((u) => u.id !== userId)
          .map((u) => u.id);
      }

      apiClient
        ?.put({
          url: APIEndPoints.MY_LISTS.ONE(parseInt(id)),
          body: data,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "User successfully removed from list.",
          });
          setRefreshNeeded(!refreshNeeded);
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            alertsManager.showAlertMessage({
              type: AlertMessageType.ERROR,
              message: "Unable to remove user from list.",
            });
          }
        });
    }
  };

  const addUserToShareList = () => {
    if (!id) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "Cannot access database records",
      });
    } else {
      // initialize the list of users already in this list
      data.id_users = [];
      if (data.users) {
        // add existing users to the list
        data.id_users = data?.users.map((u) => u.id);
      }
      data.id_users.push(candidateUserID);

      apiClient
        ?.put({
          url: APIEndPoints.MY_LISTS.ONE(parseInt(id)),
          body: data,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "User successfully added to the list.",
          });
          setRefreshNeeded(!refreshNeeded);
          setCandidateUserID(-1);
          setAddUsersToShare(false);
          setKeywordUsers("");
          setCandidateUsersList([]);
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            alertsManager.showAlertMessage({
              type: AlertMessageType.ERROR,
              message: "Unable to add the user to this list.",
            });
          }
        });
    }
  };

  const onUserChange = (
    inputId: string,
    value: number | string | undefined,
  ) => {
    if (!value) {
      setCandidateUserID(-1);
    } else if (typeof value === "string") {
      setCandidateUserID(parseInt(value));
    } else setCandidateUserID(value);
  };

  const onUserSearch = async (inputID: string, keyword: string) => {
    // we perform the search ONLY when at least three letters have been added
    if (keyword.length < 3) {
      setKeywordUsers(keyword);
      setCandidateUsersList([]);
      return;
    }

    const usersInfo = await apiClient?.get<PagedData<User[]>>({
      url: APIEndPoints.USERS.SEARCH(keyword),
    });
    setKeywordUsers(keyword);
    setCandidateUsersList(usersInfo.items);
  };

  const usersAsOptions = candidateUsersList
    .filter(
      (u) =>
        u.id !== authManager.getUserID() && !usersAlreadySharing?.has(u.id),
    )
    .map((user) => {
      return new SelectSearchOption(user.username, user.id);
    });

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() ||
      authManager.isAdmin());

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt={"List icon"}
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo className="span-3">
              {AppIcons.USER}
              created by: <span>{data.username}</span>
            </ButtonInfo>
            {/* Button to open modal */}
            <ButtonInfo>
              {AppIcons.CH_ELEMENT}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.CHELEMENT,
                )}
              </span>
              Cultural Heritage Assets
            </ButtonInfo>
          </div>

          {canEdit && (
            <div className="flex items-start mt-5">
              <Button
                label={"Delete this list"}
                purpose={ButtonAction.DELETE}
                onClick={() => {
                  alertsManager.showDialogWindow({
                    type: DialogWindowType.CONFIRM,
                    message: "Are you sure you want to delete this list?",
                    cancelLabel: "Cancel",
                    confirmLabel: "Confirm",
                    onConfirm: handleDelete,
                  });
                }}
              />

              <Button
                label={"Explore this list"}
                purpose={ButtonAction.EXPLORE}
                onClick={() => {
                  alertsManager.showGroupExplore({
                    groupID: parseInt(id!),
                    groupType: GroupingType.MY_LIST,
                    apiClient: apiClient,
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>List details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.MY_LISTS,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">
          {data.description ?? "No description provided."}
        </p>
      </div>

      {/* CHElements Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Cultural Heritage Assets in this list</h2>
          {canEdit && (
            <Button
              label={"How to add assets to this list?"}
              purpose={ButtonAction.INFO}
              type={ButtonType.ICON_TEXT}
              onClick={() => {
                alertsManager.showDialogWindow({
                  type: DialogWindowType.INFO,
                  message: "To add Cultural Heritate Assets to private list:",
                  instructions: [
                    "Search the CH Asset using any of the available functionalities.",
                    "Open the CH Asset details page",
                    "Click on the 'Add this asset to another List' button and select the desired list.",
                  ],
                  confirmLabel: "Ok",
                  onConfirm: () => {},
                });
              }}
            />
          )}
        </div>
        <div className="details-section-content">
          {(!data.chelements || data.chelements.length === 0) && (
            <div className="text-left">
              There are no Cultural Heritage Assets in this list.
            </div>
          )}
          {data.chelements &&
            data.chelements.length > 0 &&
            data.chelements.map((e) => {
              return (
                <div
                  className="details-grid-element"
                  onClick={() => {
                    navigate(
                      SETTINGS.get_path({
                        section: AppSections.CHELEMENTS,
                        id: e.id,
                        subsection: AppSubSections.VIEW,
                      }),
                    );
                  }}
                >
                  <div className="details-grid-content p-0 flex flex-row">
                    <div className="details-grid-description grow">
                      {e.name}
                    </div>
                    {canEdit && (
                      <Button
                        type={ButtonType.ICON}
                        purpose={ButtonAction.DELETE}
                        label=""
                        onClick={() => {
                          alertsManager.showDialogWindow({
                            type: DialogWindowType.CONFIRM,
                            message:
                              "Are you sure you want to remove the asset from this list?",
                            cancelLabel: "Cancel",
                            confirmLabel: "Confirm",
                            onConfirm: () => removeAssetFromCollection(e.id!),
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Shared with Users Section */}

      <div className="details-section">
        <div className="details-section-title">
          <h2>This list is shared with</h2>
          {canEdit && (
            <Button
              label={"Share with other users"}
              purpose={ButtonAction.SHARE}
              type={ButtonType.ICON_TEXT}
              onClick={() => setAddUsersToShare(true)}
            />
          )}
        </div>
        <div className="details-section-content">
          {addUsersToShare && (
            <div className="details-grid-content p-0 flex flex-row items-start mb-10">
              <div className="grow">
                <SelectWithSearch
                  options={usersAsOptions}
                  label=""
                  id="add_to_shared"
                  onChange={onUserChange}
                  value={candidateUserID}
                  noValueMessage={
                    "Type at least three letters here to search for users..."
                  }
                  onSearch={onUserSearch}
                  topCSS="form-input p-0 border-0"
                />
              </div>
              {candidateUserID !== -1 && (
                <Button
                  type={ButtonType.FILL_ICON_TEXT}
                  purpose={ButtonAction.CREATE}
                  label="Add user"
                  onClick={() => addUserToShareList()}
                />
              )}
              <Button
                type={ButtonType.FILL_ICON_TEXT}
                purpose={ButtonAction.CANCEL}
                label="Cancel"
                onClick={() => {
                  setCandidateUserID(-1);
                  setAddUsersToShare(false);
                }}
                className="ml-3"
              />
            </div>
          )}
          {(!data.users || data.users.length === 0) && (
            <div className="text-left">
              This list has not been shared yet with any users.
            </div>
          )}
          {data.users &&
            data.users.length > 0 &&
            data.users.map((u) => {
              return (
                <div className="details-grid-element">
                  <div className="details-grid-content p-0 flex flex-row">
                    <div className="details-grid-description grow">
                      {u.username}
                    </div>
                    <Button
                      type={ButtonType.ICON}
                      purpose={ButtonAction.DELETE}
                      label=""
                      onClick={() => {
                        alertsManager.showDialogWindow({
                          type: DialogWindowType.CONFIRM,
                          message:
                            "Are you sure you want to remove this user from the list?",
                          cancelLabel: "Cancel",
                          confirmLabel: "Confirm",
                          onConfirm: () => removeUserFromList(u.id),
                        });
                      }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default MyListDetails;
